/** Query key for in-page section anchors (HashRouter cannot use a second `#fragment`). */
export const DOC_SECTION_SEARCH_PARAM = "section";

/** Hash route href for the current doc page. */
export function getDocPageHashHref(pathname: string, sectionId?: string): string {
  const base = `#${pathname}`;
  if (!sectionId) {
    return base;
  }
  const params = new URLSearchParams({ [DOC_SECTION_SEARCH_PARAM]: sectionId });
  return `${base}?${params.toString()}`;
}

/** True for `#section-id` anchors, not hash routes like `#/glofox`. */
export function isDocSectionHashHref(href: string): boolean {
  return href.startsWith("#") && !href.startsWith("#/");
}

export function getDocSectionIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get(DOC_SECTION_SEARCH_PARAM);
  return value?.trim() ? value : null;
}

function getDocScrollContainer(from: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = from.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** Scroll a heading into view; accounts for sticky nav via `scroll-margin-top`. */
export function scrollToDocAnchor(
  id: string,
  behavior: ScrollBehavior = "smooth",
): boolean {
  const decoded = decodeURIComponent(id);
  const target = document.getElementById(decoded);
  if (!target) {
    return false;
  }

  const scrollMargin =
    Number.parseFloat(getComputedStyle(target).scrollMarginTop) ||
    Number.parseFloat(getComputedStyle(target).scrollMargin) ||
    72;
  const container = getDocScrollContainer(target);

  if (container) {
    const targetRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const top = container.scrollTop + (targetRect.top - containerRect.top) - scrollMargin;
    container.scrollTo({ top: Math.max(0, top), behavior });
  } else {
    const top = window.scrollY + target.getBoundingClientRect().top - scrollMargin;
    window.scrollTo({ top: Math.max(0, top), behavior });
  }

  return true;
}

/** Reset scroll position when entering a doc page without a valid section anchor. */
export function resetDocScroll(root?: HTMLElement | null): void {
  window.scrollTo({ top: 0, behavior: "auto" });
  const mainCol =
    root?.closest(".main-col") ?? document.querySelector<HTMLElement>(".main-col");
  if (mainCol instanceof HTMLElement) {
    mainCol.scrollTo({ top: 0, behavior: "auto" });
  }
}
